import ChevronDownIcon from "../../icons/ChevronDown";
import {
  Box,
  Breadcrumbs,
  Divider,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";

const RECENT_COUNT = 3;

const getBreadcrumbLinkSx = (theme: Theme) => {
  const defaultColor =
    theme.palette.mode === "dark"
      ? theme.palette.text.disabled
      : theme.palette.grey[600];

  return {
    color: defaultColor,
    "&:visited": {
      color: defaultColor,
    },
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  };
};

const RecentHistoryBreadcrumbs = () => {
  const { history } = useNavigationHistory();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (history.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5 }}>
        <Skeleton width={240} height={20} />
      </Box>
    );
  }

  // Most recent 3 entries shown in the breadcrumb trail (newest last)
  const recentEntries = history.slice(-RECENT_COUNT);
  // Older entries shown in the history menu (oldest first)
  const olderEntries = history.slice(0, -RECENT_COUNT).reverse();

  const currentEntry = recentEntries[recentEntries.length - 1];

  const handleMenuNavigate = (pathname: string, search: string) => {
    setMenuAnchor(null);
    // Let the MUI Modal backdrop fully unmount before navigating so it does
    // not absorb the next pointer event.
    setTimeout(() => navigate(`${pathname}${search ?? ""}`), 0);
  };

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        slotProps={{ paper: { sx: { minWidth: 220, maxWidth: 320 } } }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: "block" }}
        >
          Earlier
        </Typography>
        {olderEntries.map((entry) => (
          <MenuItem
            key={entry.pathname}
            onClick={() => handleMenuNavigate(entry.pathname, entry.search)}
            dense
          >
            <ListItemText
              primary={entry.label}
              primaryTypographyProps={{
                variant: "body2",
                noWrap: true,
              }}
            />
          </MenuItem>
        ))}
        {recentEntries.length > 0 && [
          <Divider key="divider" />,
          <Typography
            key="recent-label"
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, py: 0.5, display: "block" }}
          >
            Recent
          </Typography>,
          ...recentEntries.map((entry) => (
            <MenuItem
              key={entry.pathname}
              onClick={() => handleMenuNavigate(entry.pathname, entry.search)}
              dense
            >
              <ListItemText
                primary={entry.label}
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                  fontWeight:
                    entry.pathname === currentEntry.pathname ? 600 : 400,
                }}
              />
            </MenuItem>
          )),
        ]}
      </Menu>

      <Breadcrumbs
        aria-label="navigation history"
        separator="/"
        sx={{
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          "& .MuiBreadcrumbs-separator": { mx: 0.75, color: "text.secondary" },
        }}
      >
        {olderEntries.length > 0 && (
          <Link
            component="button"
            variant="body2"
            underline="always"
            aria-haspopup="true"
            aria-expanded={Boolean(menuAnchor)}
            aria-label="Show navigation history"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={(theme) => ({
              ...getBreadcrumbLinkSx(theme),
              display: "inline-flex",
              alignItems: "center",
              gap: 0,
              cursor: "pointer",
              border: "none",
              background: "none",
              p: 0,
              verticalAlign: "baseline",
            })}
          >
            Recent
            <ChevronDownIcon
              fontSize="small"
              sx={{
                mt: 0.35,
                fontSize: "18px",
                color: "inherit",
              }}
            />
          </Link>
        )}
        {recentEntries.map((entry) => {
          const isCurrent = entry.pathname === currentEntry.pathname;

          if (isCurrent) {
            return (
              <Typography
                key={entry.pathname}
                variant="body2"
                color="text.primary"
                fontWeight={500}
                noWrap
                sx={{ maxWidth: 220 }}
              >
                {entry.label}
              </Typography>
            );
          }

          const to = `${entry.pathname}${entry.search ?? ""}`;
          return (
            <Link
              key={entry.pathname}
              href={to}
              variant="body2"
              underline="always"
              noWrap
              sx={(theme) => ({
                ...getBreadcrumbLinkSx(theme),
                maxWidth: 220,
                cursor: "pointer",
              })}
              onClick={(e) => {
                e.preventDefault();
                navigate(to);
              }}
            >
              {entry.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default RecentHistoryBreadcrumbs;
